import { useState, useEffect, useCallback } from "react";
import { sanitizeInput } from "../../../utils/sanitization";

export const useTeamFormState = (squads, events) => {
  // Context state (persistent across team creations)
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form state (resets for each team)
  const [teamName, setTeamName] = useState("");
  const [startingPair, setStartingPair] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const hasFormChanges = teamName.trim() !== "" || hasChanges;
    setHasChanges(hasFormChanges);
  }, [teamName, hasChanges]);

  // Squad change handler
  const handleSquadChange = useCallback(
    (squadId) => {
      const squad = squads.find((s) => s.id === squadId);
      setSelectedSquad(squad);
      setErrors({});
    },
    [squads]
  );

  // Event change handler
  // Note: events array may be empty initially, so we handle that case
  const handleEventChange = useCallback(
    (eventId) => {
      const event = events.find((e) => e.id === eventId);
      setSelectedEvent(event || null);
      setErrors({});
    },
    [events]
  );

  // Team name change handler (no sanitization on keystroke)
  const handleTeamNameChange = useCallback((value) => {
    setTeamName(value);
    setErrors((prev) => ({ ...prev, teamName: null }));
  }, []);

  // Team name blur handler (sanitize on blur)
  const handleTeamNameBlur = useCallback((value) => {
    const sanitizedValue = sanitizeInput(value);
    setTeamName(sanitizedValue);
    setErrors((prev) => ({ ...prev, teamName: null }));
  }, []);

  // Cancel handlers
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      // No changes, close immediately
      // This would need to be handled by parent component
    }
  }, [hasChanges]);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelConfirm(false);
    // This would need to be handled by parent component
  }, []);

  const handleCancelCancel = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setTeamName("");
    setStartingPair("");
    setErrors({});
    setIsSubmitting(false);
    setShowCancelConfirm(false);
    setHasChanges(false);
  }, []);

  // Set has changes (for external control)
  const setHasChangesExternal = useCallback((value) => {
    setHasChanges(value);
  }, []);

  return {
    // State
    selectedSquad,
    selectedEvent,
    teamName,
    startingPair,
    errors,
    isSubmitting,
    showCancelConfirm,
    hasChanges,

    // Setters
    setSelectedSquad,
    setSelectedEvent,
    setTeamName,
    setStartingPair,
    setErrors,
    setIsSubmitting,
    setShowCancelConfirm,
    setHasChanges: setHasChangesExternal,

    // Handlers
    handleSquadChange,
    handleEventChange,
    handleTeamNameChange,
    handleTeamNameBlur,
    handleCancel,
    handleConfirmCancel,
    handleCancelCancel,
    resetForm,
  };
};
